// @flow

import React, { PureComponent, type Node, useContext, useEffect } from 'react';
import ErrorBoundary from 'app/packages/error-boundary';
import ExperimentOverlay from 'app/packages/debugger/ui/ExperimentOverlay';
import DebugContext, { HIGHLIGHT_EXPERIMENTS_ALL } from 'app/packages/debugger/DebugContext';

type FunctionChildProps = {|
  // What group does the user hash into? If the experiment was overrided, the
  // overridden group will be provided.
  group: string,

  // Does the group appear to be an "enabled" group? This checks to see if the
  // group starts with the string "enabled" or is "employee". Note that some
  // experiments opt employees out of the enabled treatment and others create
  // treatments that don't use the "enabled_x" naming pattern. These experiments
  // should use the `group` param instead.
  anyEnabled: boolean,

  // Any time the user is eligible and the experiment isn't activated by
  // default, a customActivate is passed to the function prop so that it may
  // activate manually.
  customActivate: ?() => string,
|};

type WrapperProps = {|
  // Is the group being overridden by the debugger?
  overrideGroup: ?string,

  // Is the debugger highlighting this experiment?
  highlighted: boolean,
|};

type OwnProps = {|
  // Whether or not to activate the user. If false, it's assumed that user will
  // call customActivate in the future.
  activate?: boolean,

  // Called when experiment is activated, either automatically on mount or on
  // call to customActivate when activate prop is false.
  activateExperiment: (string) => string,

  // Experiment uses render props to render children.
  children: (props: FunctionChildProps) => Node,

  // What group does the user hash into? If overrideGroup is specified, this is
  // ignored.
  group: string,

  // Whether or not the user is eligible for an experiment. If ineligible, no
  // activation will occur.
  isEligible?: boolean,

  // Experiment name.
  name: string,
|};

type Props = {|
  ...OwnProps,
  ...WrapperProps,
|};

export class Experiment extends PureComponent<Props> {
  static defaultProps = {
    activate: true,
    isEligible: true,
    overrideGroup: null,
    highlighted: false,
  };

  componentDidMount() {
    const { activate, activateExperiment, isEligible, name } = this.props;

    // Only activate experiment if the user is eligible.
    if (activate && isEligible) {
      activateExperiment(name);
    }
  }

  getGroup() {
    const { group, overrideGroup } = this.props;
    return typeof overrideGroup === 'string' ? overrideGroup : group;
  }

  handleCustomActivate = (): string => {
    const { activateExperiment, isEligible, name } = this.props;

    if (isEligible) {
      return activateExperiment(name);
    }

    return '';
  };

  attachDebugging = (output: Node) => {
    const { activate, name, isEligible, highlighted } = this.props;

    const group = this.getGroup();

    if (highlighted) {
      return (
        <ExperimentOverlay
          group={group}
          isEligible={isEligible}
          name={name}
          performsActivate={activate}
        >
          {output}
        </ExperimentOverlay>
      );
    }

    return output;
  };

  render() {
    const { activate, name, children, isEligible } = this.props;

    const group = this.getGroup();

    const errorBoundaryName = `Experiment(${name}:${group})`;

    const anyEnabled = isEligible
      ? group.startsWith('enabled') || group.startsWith('employee')
      : false;
    const childArgs = {
      group: isEligible ? group : '',
      anyEnabled,
      customActivate: isEligible && !activate ? this.handleCustomActivate : null,
    };

    return this.attachDebugging(
      <ErrorBoundary name={errorBoundaryName}>{children(childArgs)}</ErrorBoundary>,
    );
  }
}

// Wrap the Experiment component with a DebugContext consumer. The reason
// Experiment is divided into two parts is because components that consume
// DebugContext will re-render any time its value changes, and most of the time
// the changes will not affect the Experiment. To prevent needless re-renders of
// Experiment, we make it a PureComponent and only pass the context values that
// are needed.
export default function ExperimentWrapper(props: OwnProps) {
  const { name } = props;

  const {
    debuggingEnabled,
    overriddenExperiments,
    highlightedExperiment,
    setExperimentVisible,
  } = useContext(DebugContext);

  // Derive props for Experiment from debug context.
  const overrideGroup =
    debuggingEnabled && overriddenExperiments && typeof overriddenExperiments[name] === 'string'
      ? overriddenExperiments[name]
      : null;
  const highlighted =
    debuggingEnabled &&
    (highlightedExperiment === name || highlightedExperiment === HIGHLIGHT_EXPERIMENTS_ALL);

  // Control experiment visibility counts based on lifecycle of this component.
  // When it's in the DOM, we want DebugContext to know about it. Only enable if
  // debugging is enabled.
  useEffect(
    () => {
      if (debuggingEnabled) {
        setExperimentVisible(name, true);
        return () => setExperimentVisible(name, false);
      }

      return undefined;
    },
    [name, debuggingEnabled],
  );

  return <Experiment {...props} overrideGroup={overrideGroup} highlighted={highlighted} />;
}
